import { Action, Reducer } from "redux";
import { AppThunkAction, ApplicationState } from "./";
import { get, post } from "../fetch-interceptor";
import { catchApiError, defaultCatch, isCancel } from "./utils";
import {
    KnownAction as NotificationsKnownAction,
    actionCreators as NotificationsActionCreators,
    NotificationType
} from "./NotificationsStore";
import { Tracker } from "./PreferencesTracker";
import { saveSettings } from "./Sections";
import { toDate } from "../components/utils/common";
import { DelegatedApi, DelegationScope, onBehalfOf } from "./DelegationStore";
import { Dictionary, IUserInfo, UserPreferencesSettingsUpdate } from "../entities/common";
import { GateDecision } from "../entities/Subentities";

export enum ApprovalRequestStatus {
    Pending = 0,
    Approved = 1,
    Rejected = 2
}

export enum ApprovalRequestType {
    Timesheet = 0,
    StageTransition = 1
}

export interface ApprovalDecision {
    approver: IUserInfo;
    decision: ApprovalDecisionType;
    decisionDate: Date;
    comment: string | null;
    data?: ApprovalDecisionData;
}

export enum ApprovalDecisionType {
    Approved = 0,
    Rejected = 1
}

export type ApprovalDecisionData = Record<string, any>;

export interface StageGateDecisionData {
    gateDecision: GateDecision;
}
export const isStageGateDecisionData = (data: ApprovalDecisionData | undefined): data is StageGateDecisionData =>
    typeof data?.gateDecision === "number";

export interface ApprovalRequestAttrs extends Dictionary<any> {
    Status: ApprovalRequestStatus;
    Submitter: IUserInfo;
    SubmittedDate: Date;
    SubmissionComment: string | null;
    Approvers: IUserInfo[];
}

export interface ApprovalRequest<TDetails = unknown> {
    id: string;
    attributes: ApprovalRequestAttrs;
    type: ApprovalRequestType;
    details: TDetails | null;
    decisions: ApprovalDecision[];
}

export interface ApprovalStoreState<TDetails> {
    requests: ApprovalRequest<TDetails>[];
    isLoading: boolean;
    pendingCount: number;
    isSettingsLoading: boolean;
    settings: any;
}

export interface ApprovalActionCreators {
    loadRequests: (status?: ApprovalRequestStatus) => any;
    approveRequests: (requestIds: string[], comment?: string, callback?: () => void, payload?: Record<string, any>) => any;
    rejectRequests: (requestIds: string[], comment?: string, callback?: () => void, payload?: Record<string, any>) => any;
    recallRequests: (requestIds: string[], callback?: () => void) => any;
}

export interface StoreActions<TDetails> {
    type: string;
    requests?: ApprovalRequest<TDetails>[];
    requestIds?: string[];
    count?: number;
    settings?: any;
    update?: UserPreferencesSettingsUpdate;
}

export function createApprovalStore<TDetails>(
    approvalType: ApprovalRequestType,
    namespace: string,
    parseDetailsDates?: (details: TDetails) => void,
    delegationScope?: DelegationScope
) {
    const AT = {
        LOAD_REQUESTS_REQUEST: `${namespace}/LOAD_REQUESTS_REQUEST`,
        LOAD_REQUESTS_SUCCESS: `${namespace}/LOAD_REQUESTS_SUCCESS`,
        LOAD_REQUESTS_FAILURE: `${namespace}/LOAD_REQUESTS_FAILURE`,
        APPROVE_REQUEST: `${namespace}/APPROVE_REQUEST`,
        APPROVE_SUCCESS: `${namespace}/APPROVE_SUCCESS`,
        REJECT_REQUEST: `${namespace}/REJECT_REQUEST`,
        REJECT_SUCCESS: `${namespace}/REJECT_SUCCESS`,
        RECALL_REQUEST: `${namespace}/RECALL_REQUEST`,
        RECALL_SUCCESS: `${namespace}/RECALL_SUCCESS`,
        LOAD_PENDING_COUNT_SUCCESS: `${namespace}/LOAD_PENDING_COUNT_SUCCESS`,
        LOAD_SETTINGS: `${namespace}/LOAD_SETTINGS`,
        LOADED_SETTINGS: `${namespace}/LOADED_SETTINGS`,
        SAVE_SETTINGS: `${namespace}/SAVE_SETTINGS`
    };

    const parseDates = (requests: ApprovalRequest<TDetails>[]) => {
        for (const r of requests) {
            r.attributes.SubmittedDate = toDate(r.attributes.SubmittedDate)!;
            for (const d of r.decisions) {
                d.decisionDate = toDate(d.decisionDate)!;
            }
            if (r.details && parseDetailsDates) {
                parseDetailsDates(r.details);
            }
        }
        return requests;
    };

    const requestApi = (dispatch: (action: any) => void, getState: () => ApplicationState): DelegatedApi =>
        delegationScope === undefined ? { get, post } : onBehalfOf(delegationScope, dispatch, getState);

    const refreshPendingCount = (
        dispatch: (action: any) => void,
        getState: () => ApplicationState
    ) => {
        requestApi(dispatch, getState)
            .get<number>(
                "api/approval-center/pendingCount",
                { type: approvalType },
                { requestKey: `api/approval-center/pendingCount?type=${approvalType}` }
            )
            .then(count => dispatch({ type: AT.LOAD_PENDING_COUNT_SUCCESS, count }))
            .catch(defaultCatch(dispatch));
    };

    const applyDecisionResult =
        (dispatch: (action: any) => void, getState: () => ApplicationState, successType: string, callback?: () => void) =>
        (requests: ApprovalRequest<TDetails>[]) => {
            dispatch({ type: successType, requests: parseDates(requests) });
            refreshPendingCount(dispatch, getState);
            callback?.();
        };

    const notifyDecisionFailure = (dispatch: (action: any) => void, successType: string) =>
        catchApiError(message => {
            dispatch(NotificationsActionCreators.pushNotification({ message, type: NotificationType.Error }));
            dispatch({ type: successType, requests: [] });
        });

    const postDecision =
        (endpoint: string, requestType: string, successType: string) =>
        (
            requestIds: string[],
            comment?: string,
            callback?: () => void,
            payload?: Record<string, any>
        ): AppThunkAction<StoreActions<TDetails> | NotificationsKnownAction> =>
        (dispatch, getState) => {
            dispatch({ type: requestType });
            requestApi(dispatch, getState)
                .post<ApprovalRequest<TDetails>[]>(endpoint, { requestIds, comment, payload })
                .then(applyDecisionResult(dispatch, getState, successType, callback))
                .catch(notifyDecisionFailure(dispatch, successType));
        };

    const actionCreators = {
        loadRequests:
            (status?: ApprovalRequestStatus): AppThunkAction<StoreActions<TDetails>> =>
            (dispatch, getState) => {
                dispatch({ type: AT.LOAD_REQUESTS_REQUEST });
                requestApi(dispatch, getState)
                    .get<ApprovalRequest<TDetails>[]>(
                        "api/approval-center",
                        { type: approvalType, status },
                        { requestKey: `api/approval-center?type=${approvalType}` }
                    )
                    .then(requests => dispatch({ type: AT.LOAD_REQUESTS_SUCCESS, requests: parseDates(requests) }))
                    .catch(error => {
                        if (!isCancel(error)) {
                            dispatch({ type: AT.LOAD_REQUESTS_FAILURE });
                        }
                        defaultCatch(dispatch)(error);
                    });
            },

        approveRequests: postDecision("api/approval-center/approve", AT.APPROVE_REQUEST, AT.APPROVE_SUCCESS),

        rejectRequests: postDecision("api/approval-center/reject", AT.REJECT_REQUEST, AT.REJECT_SUCCESS),

        recallRequests:
            (
                requestIds: string[],
                callback?: () => void
            ): AppThunkAction<StoreActions<TDetails> | NotificationsKnownAction> =>
            (dispatch, getState) => {
                dispatch({ type: AT.RECALL_REQUEST });
                post<void>("api/approval-center/recall", { requestIds })
                    .then(() => {
                        dispatch({ type: AT.RECALL_SUCCESS, requestIds });
                        refreshPendingCount(dispatch, getState);
                        callback?.();
                    })
                    .catch(
                        catchApiError(_ => {
                            dispatch(
                                NotificationsActionCreators.pushNotification({ message: _, type: NotificationType.Error })
                            );
                            dispatch({ type: AT.RECALL_SUCCESS, requestIds: [] });
                        })
                    );
            },

        loadPendingCount: (): AppThunkAction<StoreActions<TDetails>> =>
            (dispatch, getState) => refreshPendingCount(dispatch, getState),

        markSettingsLoading: (): StoreActions<TDetails> => ({ type: AT.LOAD_SETTINGS }),

        applyLoadedSettings: (blob: Dictionary<any>): StoreActions<TDetails> => ({
            type: AT.LOADED_SETTINGS,
            settings: blob?.[namespace] ?? blob ?? {}
        }),

        saveSettings:
            (update: UserPreferencesSettingsUpdate, sendToServer: boolean = true): AppThunkAction<StoreActions<TDetails>> =>
            (dispatch, getState) => {
                dispatch({ type: AT.SAVE_SETTINGS, update });
                if (sendToServer) {
                    Tracker.setApprovalSettings(dispatch, getState, {
                        ...update,
                        parentSettingsPathKeys: [namespace, ...update.parentSettingsPathKeys]
                    });
                }
            }
    };

    const initialState: ApprovalStoreState<TDetails> = {
        requests: [],
        isLoading: true,
        pendingCount: 0,
        isSettingsLoading: false,
        settings: {}
    };

    const reducer: Reducer<ApprovalStoreState<TDetails>> = (
        state: ApprovalStoreState<TDetails> = initialState,
        incomingAction: Action
    ) => {
        const action = incomingAction as StoreActions<TDetails>;

        switch (action.type) {
            case AT.LOAD_REQUESTS_REQUEST:
            case AT.APPROVE_REQUEST:
            case AT.REJECT_REQUEST:
            case AT.RECALL_REQUEST:
                return { ...state, isLoading: true };

            case AT.LOAD_REQUESTS_SUCCESS:
                return { ...state, isLoading: false, requests: action.requests ?? [] };

            case AT.LOAD_REQUESTS_FAILURE:
                return { ...state, isLoading: false };

            case AT.APPROVE_SUCCESS:
            case AT.REJECT_SUCCESS:
                return {
                    ...state,
                    isLoading: false,
                    requests: state.requests.map(r => {
                        const updated = (action.requests ?? []).find(u => u.id === r.id);
                        return updated ?? r;
                    })
                };

            case AT.RECALL_SUCCESS: {
                const recalledIds = new Set(action.requestIds ?? []);
                return {
                    ...state,
                    isLoading: false,
                    requests: state.requests.filter(r => !recalledIds.has(r.id))
                };
            }

            case AT.LOAD_PENDING_COUNT_SUCCESS:
                return { ...state, pendingCount: action.count ?? 0 };

            case AT.LOAD_SETTINGS:
                return { ...state, isSettingsLoading: true };

            case AT.LOADED_SETTINGS:
                return { ...state, isSettingsLoading: false, settings: action.settings };

            case AT.SAVE_SETTINGS:
                return saveSettings(state, action.update!);

            default:
                return state;
        }
    };

    return { actionCreators, requestApi, reducer };
}

export type ApprovalSettingsApplier = {
    markLoading: () => Action;
    applyLoaded: (blob: Dictionary<any>) => Action;
};

export const loadApprovalSettings =
    (appliers: ApprovalSettingsApplier[]): AppThunkAction<Action> =>
    dispatch => {
        for (const a of appliers) {
            dispatch(a.markLoading());
        }
        get<Dictionary<any>>("api/user/preferences/approval")
            .then(blob => {
                for (const a of appliers) {
                    dispatch(a.applyLoaded(blob));
                }
            })
            .catch(defaultCatch(dispatch));
    };
